import { type ThemeMode, DEFAULT_MODE, isMode, DEFAULT_THEME } from "../default-theme";
import { STORAGE_KEYS } from "../storage-keys";
import { isThemeId } from "../is-theme-id";
import { getPublicEnv } from "@/config/get-public-env";

/** Sets data-theme, the .dark class and color-scheme on <html>. */
export function applyToDocument(theme: string, mode: ThemeMode) {
  const d = document.documentElement;
  const dark = mode === "dark" || (mode === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  d.setAttribute("data-theme", theme);
  d.classList.toggle("dark", dark);
  d.style.colorScheme = dark ? "dark" : "light";
}

/** Reads a validated value from localStorage, falling back when missing, invalid or unavailable. */
function readSetting<T>(key: string, ok: (v: unknown) => v is T, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return ok(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

/** The mode the visitor chose, else the one the page was given (Settings > App brand), else the factory one. */
export const getMode = () => readSetting(STORAGE_KEYS.mode, isMode, siteDefaultMode());

function siteDefaultMode() {
  const given = document.documentElement.dataset.defaultMode;
  return isMode(given) ? given : DEFAULT_MODE;
}

export const getTheme = () => readSetting(STORAGE_KEYS.theme, isThemeId, DEFAULT_THEME);

/** Stores a setting in localStorage and a cookie (for later server-side use). */
function persistSetting(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
  const maxAge = getPublicEnv().NEXT_PUBLIC_SETTINGS_COOKIE_MAX_AGE_SECONDS;
  document.cookie = `${key}=${value}; path=/; max-age=${maxAge}; samesite=lax`;
}

const THEME_CHANGE_EVENT = "saads-theme-change";

export function setMode(mode: ThemeMode) {
  persistSetting(STORAGE_KEYS.mode, mode);
  applyToDocument(getTheme(), mode);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

export function setTheme(theme: string) {
  if (!isThemeId(theme)) return;
  persistSetting(STORAGE_KEYS.theme, theme);
  applyToDocument(theme, getMode());
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

/** useSyncExternalStore subscriber: our event, other tabs, and the OS colour scheme. */
export function subscribeTheme(notify: () => void) {
  const mq = matchMedia("(prefers-color-scheme: dark)");
  const onSystem = () => {
    applyToDocument(getTheme(), getMode());
    notify();
  };
  window.addEventListener(THEME_CHANGE_EVENT, notify);
  window.addEventListener("storage", notify);
  mq.addEventListener("change", onSystem);
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, notify);
    window.removeEventListener("storage", notify);
    mq.removeEventListener("change", onSystem);
  };
}
